import { useState } from "react";
import { createPortal } from "react-dom";
import {
  Save,
  Loader2,
  Eye,
  EyeOff,
  GripVertical,
  X,
  Lock,
  Film,
  Type,
  LayoutGrid,
  Youtube,
  BookOpen,
  Images,
  Music,
  CircleDot,
  MessageCircle,
  Link2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { updateCampaign } from "@/api/client";
import {
  DEFAULT_JOURNEYS,
  DEFAULT_GIRI_WHATSAPP,
  DEFAULT_BOOK_URL,
  PATH_TAGS,
} from "@/config/journeys";
import { VideoCarousel } from "@/components/journey/VideoCarousel";
import { CantoCarousel } from "@/components/journey/CantoCarousel";
import { Gallery } from "@/components/journey/Gallery";
import { PhotoStory } from "@/components/journey/PhotoStory";
import { MalaCounter } from "@/components/journey/MalaCounter";
import { AudioSample } from "@/components/journey/AudioSample";
import { ConversionCTA } from "@/components/journey/ConversionCTA";
import { DragHandleList } from "./DragHandleList";
import { ModuleSettings } from "./ModuleSettings";

const clone = (o) => JSON.parse(JSON.stringify(o));
const PATH_ORDER = ["bhagavatam", "mahaprasadam", "chanting", "darshan"];
const PATH_LABELS = {
  bhagavatam: "Bhagavatam",
  mahaprasadam: "Prasadam",
  chanting: "Chanting",
  darshan: "Darshan",
};
const TYPE_META = {
  video_carousel: { label: "Video Carousel", Icon: Youtube },
  canto_carousel: { label: "Canto Carousel", Icon: BookOpen },
  gallery: { label: "Image Gallery", Icon: Images },
  photo_story: { label: "Arrival Story", Icon: Images },
  mala_counter: { label: "Mala Counter", Icon: CircleDot },
  audio_sample: { label: "Audio Sample", Icon: Music },
  conversion: { label: "End Card (Name + CTA)", Icon: MessageCircle },
};
const FIXED_SECTIONS = [
  { key: "opening", label: "Opening Story", Icon: Film, note: "The cinematic scroll intro (Krishna's birth). Fixed brand experience." },
  { key: "invitation", label: "Invitation", Icon: Type, note: '"Take Krishna Home" invitation + "Show me how". Fixed brand experience.' },
  { key: "cards", label: "Sacred Paths · Scratch Cards", Icon: LayoutGrid, note: "The four scratch-to-reveal cards. Edit their text & links in the CTA cards panel above." },
];
const RENDERERS = {
  video_carousel: VideoCarousel,
  canto_carousel: CantoCarousel,
  gallery: Gallery,
  photo_story: PhotoStory,
  mala_counter: MalaCounter,
  audio_sample: AudioSample,
};

export const PageEditor = ({ campaignId, campaign }) => {
  const [journeys, setJourneys] = useState(() =>
    campaign.journeys && Object.keys(campaign.journeys).length ? clone(campaign.journeys) : clone(DEFAULT_JOURNEYS)
  );
  const [giri, setGiri] = useState(campaign.giri_whatsapp || "");
  const [book, setBook] = useState(campaign.book_url || "");
  const [saving, setSaving] = useState(false);
  const pathKeys = PATH_ORDER.filter((k) => journeys[k]);
  const [activePath, setActivePath] = useState(pathKeys[0] || "bhagavatam");
  const [selected, setSelected] = useState(null); // {kind:'module'|'settings'|'fixed', id/key}

  const modules = journeys[activePath]?.modules || [];

  const patchModule = (id, patch) =>
    setJourneys((j) => ({
      ...j,
      [activePath]: { ...j[activePath], modules: j[activePath].modules.map((m) => (m.id === id ? { ...m, ...patch } : m)) },
    }));
  const reorder = (next) =>
    setJourneys((j) => ({ ...j, [activePath]: { ...j[activePath], modules: next } }));
  const toggle = (id) => {
    const m = modules.find((x) => x.id === id);
    patchModule(id, { visible: m.visible === false });
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateCampaign(campaignId, { journeys, giri_whatsapp: giri, book_url: book });
      toast.success("Page saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const renderModule = (m) => {
    if (m.type === "conversion")
      return (
        <ConversionCTA
          module={m}
          giriWhatsapp={giri || DEFAULT_GIRI_WHATSAPP}
          bookUrl={book || DEFAULT_BOOK_URL}
          slug={campaign.slug}
          pathLabel={PATH_TAGS[activePath] || activePath}
        />
      );
    const R = RENDERERS[m.type];
    return R ? <R module={m} /> : null;
  };

  const selModule = selected?.kind === "module" ? modules.find((m) => m.id === selected.id) : null;

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">Visual Page Editor</h2>
        </div>
        <button
          data-testid="pageeditor-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save page
        </button>
      </div>

      <div className="flex min-h-[620px] overflow-hidden rounded-2xl border border-amber-300/12 bg-[#0d1226]/70">
        {/* LEFT RAIL */}
        <aside className="w-64 shrink-0 overflow-y-auto border-r border-amber-300/10 bg-[#0a0f24]/60 p-3 text-sm">
          <p className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-100/50">Page</p>
          {FIXED_SECTIONS.map((f) => (
            <button
              key={f.key}
              data-testid={`rail-fixed-${f.key}`}
              onClick={() => setSelected({ kind: "fixed", key: f.key })}
              className={`mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-amber-100/70 transition-colors hover:bg-amber-400/5 ${
                selected?.kind === "fixed" && selected.key === f.key ? "bg-amber-400/10 text-amber-100" : ""
              }`}
            >
              <f.Icon className="h-4 w-4 shrink-0 text-amber-300/70" />
              <span className="flex-1 truncate">{f.label}</span>
              <Lock className="h-3 w-3 text-amber-100/25" />
            </button>
          ))}

          <div className="my-3 flex items-center gap-2 px-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-100/50">Journeys (Tabs)</span>
          </div>

          {/* path tabs */}
          <div className="mb-2 flex flex-wrap gap-1 px-1">
            {pathKeys.map((k) => (
              <button
                key={k}
                data-testid={`rail-path-${k}`}
                onClick={() => {
                  setActivePath(k);
                  setSelected(null);
                }}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-all ${
                  activePath === k ? "bg-amber-400 text-[#0a0f24]" : "border border-amber-300/20 text-amber-100/60 hover:bg-amber-400/10"
                }`}
              >
                {PATH_LABELS[k] || k}
              </button>
            ))}
          </div>

          {/* modules of active path (draggable) */}
          <DragHandleList
            className="space-y-1"
            items={modules}
            getKey={(m) => m.id}
            onReorder={reorder}
            renderItem={(m, i, dh) => {
              const meta = TYPE_META[m.type] || { label: m.type, Icon: LayoutGrid };
              const on = m.visible !== false;
              const isSel = selected?.kind === "module" && selected.id === m.id;
              return (
                <div
                  data-testid={`rail-module-${m.id}`}
                  className={`flex items-center gap-1.5 rounded-lg px-1.5 py-2 transition-colors ${
                    isSel ? "bg-amber-400/10" : "hover:bg-amber-400/5"
                  } ${on ? "" : "opacity-50"}`}
                >
                  <button {...dh} data-testid={`rail-drag-${m.id}`} className="cursor-grab rounded p-0.5 text-amber-100/30 hover:text-amber-200 active:cursor-grabbing">
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <button onClick={() => setSelected({ kind: "module", id: m.id })} className="flex flex-1 items-center gap-2 truncate text-left text-amber-100/80">
                    <meta.Icon className="h-3.5 w-3.5 shrink-0 text-amber-300/70" />
                    <span className="truncate">{m.title || meta.label}</span>
                  </button>
                  <button
                    data-testid={`rail-eye-${m.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(m.id);
                    }}
                    title={on ? "Visible — click to hide" : "Hidden — click to show"}
                    className="rounded p-1 text-amber-100/40 hover:text-amber-200"
                  >
                    {on ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                </div>
              );
            }}
          />

          <button
            data-testid="rail-settings"
            onClick={() => setSelected({ kind: "settings" })}
            className={`mt-3 flex w-full items-center gap-2 rounded-lg border border-amber-300/15 px-2.5 py-2 text-left text-amber-100/70 hover:bg-amber-400/5 ${
              selected?.kind === "settings" ? "bg-amber-400/10 text-amber-100" : ""
            }`}
          >
            <Link2 className="h-4 w-4 shrink-0 text-amber-300/70" />
            <span className="flex-1 truncate">Conversion &amp; Links</span>
          </button>
        </aside>

        {/* CENTER CANVAS */}
        <main data-testid="pageeditor-canvas" className="flex-1 overflow-y-auto bg-[#05070f] p-6">
          <div className="mx-auto max-w-md space-y-4">
            {FIXED_SECTIONS.map((f) => (
              <button
                key={f.key}
                onClick={() => setSelected({ kind: "fixed", key: f.key })}
                className={`flex w-full items-center gap-3 rounded-xl border border-dashed px-4 py-4 text-left transition-colors ${
                  selected?.kind === "fixed" && selected.key === f.key ? "border-amber-400/60 bg-amber-400/5" : "border-amber-300/15 hover:border-amber-300/30"
                }`}
              >
                <f.Icon className="h-5 w-5 text-amber-300/70" />
                <div>
                  <p className="text-sm text-amber-100/85">{f.label}</p>
                  <p className="text-[11px] text-amber-100/40">Fixed brand section</p>
                </div>
              </button>
            ))}

            <div className="rounded-xl border border-amber-300/10 bg-[#070b18]/60 p-2">
              <p className="px-2 py-1 text-[11px] uppercase tracking-wider text-amber-300/60">
                Journey · {PATH_LABELS[activePath]}
              </p>
              <div className="space-y-3">
                {modules.map((m) => {
                  const isSel = selected?.kind === "module" && selected.id === m.id;
                  const hidden = m.visible === false;
                  return (
                    <div
                      key={m.id}
                      data-testid={`canvas-module-${m.id}`}
                      className={`relative overflow-hidden rounded-xl border transition-all ${
                        isSel ? "border-amber-400 ring-2 ring-amber-400/50" : "border-transparent hover:border-amber-300/25"
                      } ${hidden ? "opacity-40" : ""}`}
                    >
                      <div className="pointer-events-none origin-top scale-[0.92] py-6">{renderModule(m)}</div>
                      <button
                        aria-label={`Select ${m.type}`}
                        onClick={() => setSelected({ kind: "module", id: m.id })}
                        className="absolute inset-0"
                      />
                      {hidden && (
                        <span className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                          Hidden
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* RIGHT DRAWER (portaled to body so it sits above the sticky header) */}
      {selected &&
        createPortal(
          <div className="fixed inset-y-0 right-0 z-[60] flex w-full max-w-[380px] flex-col border-l border-amber-300/15 bg-[#0d1226] shadow-2xl">
          <div className="flex items-center justify-between border-b border-amber-300/10 px-4 py-3">
            <p className="text-sm font-semibold text-amber-100" data-testid="drawer-title">
              {selected.kind === "settings"
                ? "Conversion & Links"
                : selected.kind === "fixed"
                ? FIXED_SECTIONS.find((f) => f.key === selected.key)?.label
                : TYPE_META[selModule?.type]?.label || "Section"}
            </p>
            <button data-testid="drawer-close" onClick={() => setSelected(null)} className="rounded-full p-1.5 text-amber-100/60 hover:bg-white/10 hover:text-amber-100">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {selected.kind === "settings" && (
              <div className="grid gap-3">
                <div>
                  <label className="mb-1 block text-[11px] text-amber-100/50">GiriChat WhatsApp number (with country code)</label>
                  <input data-testid="drawer-giri" value={giri} onChange={(e) => setGiri(e.target.value)} placeholder="917208920560" className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] text-amber-100/50">Book Your Copy — purchase URL</label>
                  <input data-testid="drawer-book" value={book} onChange={(e) => setBook(e.target.value)} placeholder="https://…" className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50" />
                </div>
              </div>
            )}
            {selected.kind === "fixed" && (
              <p className="text-sm leading-relaxed text-amber-100/60">
                {FIXED_SECTIONS.find((f) => f.key === selected.key)?.note}
              </p>
            )}
            {selected.kind === "module" && selModule && (
              <ModuleSettings module={selModule} campaignId={campaignId} patch={(patch) => patchModule(selModule.id, patch)} />
            )}
          </div>
          <div className="border-t border-amber-300/10 p-3">
            <button onClick={save} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-4 py-2.5 text-xs font-semibold text-[#0a0f24] hover:bg-amber-300 disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save page
            </button>
          </div>
        </div>,
          document.body
        )}
    </div>
  );
};
